/**
 * Utilitário de Segurança Defensiva do FilaSaúde
 * Especialista: security-auditor
 * 
 * Proteções:
 * 1. Detecção e neutralização de SQL Injection (SQLi)
 * 2. Neutralização de PostgREST Filter Injection
 * 3. Sanitização e normalização segura de entradas de busca e login
 */

export class SecurityUtil {
    // Padrões clássicos e avançados de SQL Injection
    private static readonly SQL_INJECTION_PATTERNS: RegExp[] = [
        /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|TRUNCATE|EXEC(UTE)?|UNION|MERGE)\b)/i,
        /(\b(OR|AND)\s+['"]?\w+['"]?\s*=\s*['"]?\w+['"]?)/i, // OR '1'='1'
        /(--|\/\*|\*\/|;|@@|char\(|nchar\(|varchar\()/i,      // Comentários e terminadores
        /(\b(WAITFOR\s+DELAY|BENCHMARK|SLEEP|PG_SLEEP)\b)/i,  // Time-based blind SQLi
        /(['"]\s*(OR|AND)\s*[\d\w]+\s*=\s*[\d\w]+)/i,       // Injeção booleana
        /(\b(XP_CMDSHELL|SP_EXECUTESQL|LOAD_FILE|INTO\s+OUTFILE)\b)/i
    ];

    // Caracteres perigosos para PostgREST (podem quebrar cláusulas .or(), .filter())
    private static readonly POSTGREST_DANGEROUS_CHARS = /[(),\r\n\t]/g;

    /**
     * Verifica se uma string contém potenciais vetores de ataque SQL Injection.
     * Retorna true se houver indício de injeção maliciosa.
     */
    static isPotentialSqlInjection(input: unknown): boolean {
        if (!input || typeof input !== 'string') return false;

        const normalized = input.trim();
        if (normalized.length === 0) return false;

        for (const pattern of this.SQL_INJECTION_PATTERNS) {
            if (pattern.test(normalized)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Sanitiza termos de pesquisa para uso seguro em consultas PostgREST / RPC / SQL.
     * Remove caracteres de controle, aspas desbalanceadas e caracteres que alteram a sintaxe do PostgREST.
     */
    static sanitizeSearchTerm(term: string): string {
        if (!term || typeof term !== 'string') return '';

        let sanitized = term
            .replace(this.POSTGREST_DANGEROUS_CHARS, ' ') // Remove parênteses e vírgulas que quebram o .or()
            .replace(/['"`;]/g, '')                         // Remove aspas simples, duplas, backticks e ponto-e-vírgula
            .replace(/--/g, '')                             // Remove traço duplo de comentário SQL
            .replace(/\/\*[\s\S]*?\*\//g, '')               // Remove comentários multilinhas
            .replace(/\s+/g, ' ')                           // Normaliza múltiplos espaços
            .trim();

        // Limita o comprimento do termo para prevenir DoS por buffer
        if (sanitized.length > 100) {
            sanitized = sanitized.substring(0, 100);
        }

        return sanitized;
    }

    /**
     * Valida credenciais (login / código) contra caracteres proibidos.
     * Garante que o login do operador contenha apenas caracteres alfanuméricos e caracteres seguros (. _ - @).
     */
    static isValidSafeIdentifier(value: string): boolean {
        if (!value || typeof value !== 'string') return false;
        // Permite letras, números, hífen, underline, ponto e arroba (ex: operador.ubs_1)
        const safeRegex = /^[a-zA-Z0-9._@-]+$/;
        return safeRegex.test(value.trim());
    }

    /**
     * Sanitiza e valida strings genéricas de texto livre, removendo payloads perigosos.
     */
    static sanitizePlainText(text: string, maxLength = 500): string {
        if (!text || typeof text !== 'string') return '';
        return text
            .replace(/<[^>]*>?/gm, '') // Remove tags HTML/XSS básicas
            .replace(/['";]/g, '')     // Remove aspas e ponto-e-vírgula
            .substring(0, maxLength)
            .trim();
    }
}
